import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import 'moment/locale/pt-br';
import { ChevronRightIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { documentAuditService, type DocumentAuditDto } from '../services/documentAuditService';
import { formatDocumentAuditDetailsFriendly } from '../utils/documentAuditDetailsFriendly';
import { documentAuditActionLabel, documentAuditCategoryLabel } from '../utils/documentAuditDisplay';
import { useToast } from './UI/ToastProvider';
import { AuditCsvExportButton } from './AuditCsvExportButton';

moment.locale('pt-br');

interface Props {
  documentId: string;
  variant?: 'default' | 'embedded';
}

type CategoryTab = 'all' | 'Document' | 'Structural' | 'Relationship' | 'Access' | 'Signature';

const TABS: { id: CategoryTab; label: string }[] = [
  { id: 'all', label: 'Todos' },
  { id: 'Document', label: 'Documento' },
  { id: 'Structural', label: 'Estrutural' },
  { id: 'Relationship', label: 'Relacionamento' },
  { id: 'Access', label: 'Acesso' },
  { id: 'Signature', label: 'Assinatura' },
];

function categoryBadgeClass(category: string): string {
  switch (category) {
    case 'Document':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'Structural':
      return 'bg-violet-100 text-violet-800 border-violet-200';
    case 'Relationship':
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'Access':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    case 'Signature':
      return 'bg-indigo-100 text-indigo-800 border-indigo-200';
    case 'Workflow':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
}

export const DocumentAuditTimeline: React.FC<Props> = ({ documentId, variant = 'default' }) => {
  const embedded = variant === 'embedded';
  const storageKey = `audit-timeline-${documentId}`;
  const [expanded, setExpanded] = useState(() => {
    if (embedded) return true;
    const v = localStorage.getItem(storageKey);
    if (v === null) return true;
    return v === 'true';
  });
  useEffect(() => {
    if (embedded) return;
    localStorage.setItem(storageKey, String(expanded));
  }, [expanded, storageKey, embedded]);

  const { addToast } = useToast();
  const [tab, setTab] = useState<CategoryTab>('all');
  const [items, setItems] = useState<DocumentAuditDto[] | null>(null);
  const [loading, setLoading] = useState(true);

  const categoryParam = useMemo(() => (tab === 'all' ? undefined : tab), [tab]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void documentAuditService
      .getByDocument(documentId, { category: categoryParam })
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setItems([]);
          addToast(err instanceof Error ? err.message : 'Erro ao carregar auditoria do documento', 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [documentId, categoryParam, addToast]);

  const exportSuffix = tab === 'all' ? '' : `-${tab.toLowerCase()}`;

  return (
    <div className={embedded ? 'pt-0' : 'border-t pt-4'}>
      {!embedded && (
        <button
          type="button"
          className="mb-3 flex w-full items-center justify-between gap-2 text-left"
          onClick={() => setExpanded((e) => !e)}
        >
          <h3 className="text-sm font-medium text-gray-500">Histórico Geral do Documento</h3>
          <ChevronRightIcon
            className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>
      )}

      {(embedded || expanded) && (
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-1 border-b border-gray-100 pb-2 sm:border-0 sm:pb-0">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors ${
                    tab === t.id
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <AuditCsvExportButton
              exportFn={() => documentAuditService.exportCsv(documentId, categoryParam ?? null)}
              fileName={`auditoria-documento-${documentId}${exportSuffix}.csv`}
            />
          </div>

          {loading && (
            <div className="space-y-4 py-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3 animate-pulse">
                  <div className="h-10 w-10 rounded-full bg-gray-200" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-[75%] rounded bg-gray-200" />
                    <div className="h-3 w-[45%] rounded bg-gray-200" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!loading && items && items.length === 0 && (
            <p className="text-sm text-gray-400 italic">Nenhum registro de auditoria nesta categoria</p>
          )}

          {!loading && items && items.length > 0 && (
            <ul className="relative space-y-0 before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-px before:bg-gray-200">
              {items.map((row) => {
                const rel = moment(row.createdAt).fromNow();
                const full = moment(row.createdAt).format('LLL');
                const detailsFriendly = formatDocumentAuditDetailsFriendly(row.actionName, row.actionDetails);
                const actor =
                  row.userName?.trim() ||
                  (row.userId && row.userId !== '00000000-0000-0000-0000-000000000000'
                    ? 'Usuário'
                    : 'Sistema');

                return (
                  <li key={row.id} className="relative flex gap-3 pb-6 last:pb-0">
                    <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-gray-200 text-gray-500">
                      <ClipboardDocumentListIcon className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{actor}</span>
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${categoryBadgeClass(
                            row.category
                          )}`}
                        >
                          {documentAuditCategoryLabel(row.category)}
                        </span>
                        <span className="text-xs text-gray-500" title={full}>
                          {rel}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-800">{documentAuditActionLabel(row.actionName)}</p>
                      {row.actionDescription?.trim() && (
                        <p className="text-sm text-gray-600">{row.actionDescription}</p>
                      )}
                      {detailsFriendly && (
                        <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-gray-800">
                          {detailsFriendly}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
